import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Clock, LoaderCircle, SearchX, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@voll-intelligence/ui-kit/ui/alert';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { RailArrow, useRail } from '../../shared/rail.js';
import { useMediaQuery } from '@voll-intelligence/ui-kit/hooks/use-media-query';
import type { HotelFavoriteToggle, HotelMapView, HotelOfferView } from '../models.js';
import { HOTEL_NARROW_QUERY } from '../search/hotel_narrow.js';
import { HotelSearchBar, type HotelSearchBarProps } from '../search/hotel_search_bar.js';
import { HotelMap, type HotelMapProps } from './hotel_map.js';
import { HotelOfferCard } from './hotel_offer_card.js';
import { HotelResultsFooter, type HotelResultsShare } from './hotel_results_footer.js';
import './hotel_results.css';

export type HotelResultsCopy = {
  resultsLabel: string;
  previousResultsLabel: string;
  nextResultsLabel: string;
  expandLabel: string;
  mapExpandLabel: string;
  mapZoomInLabel: string;
  mapZoomOutLabel: string;
  mapRecenterLabel: string;
  retryLabel: string;
  adjustLabel: string;
  previousPhotoLabel: string;
  nextPhotoLabel: string;
  detailsLabel: string;
  selectLabel: string;
};

/**
 * Os gestos do item que acende o pino do hotel no mapa.
 *
 * Hover e foco são dois gestos para UM destaque, então o item só o solta quando
 * perde as duas mãos: tirar o mouse de uma linha que continua focada — ou
 * tabular para fora de uma que continua sob o cursor — apagava o pino com o
 * outro gesto ainda de pé.
 *
 * Quem responde "ainda tenho?" é a plataforma: `:hover` para o ponteiro, e o
 * elemento que RECEBE o foco (`relatedTarget`, porque no `focusout` o
 * `activeElement` ainda não mudou) para o teclado. Dois estados espelhando o
 * que o DOM já sabe seria escrituração para o mesmo resultado.
 */
export function offerFocusHandlers(hotelId: string, onOfferFocus: (hotelId?: string) => void) {
  const keep = (item: HTMLElement, next: EventTarget | null) =>
    (next instanceof Node && item.contains(next)) || item.matches(':hover');
  const take = () => {
    onOfferFocus(hotelId);
  };
  return {
    onMouseEnter: take,
    onFocus: take,
    onMouseLeave: (event: ReactMouseEvent<HTMLLIElement>) => {
      if (!keep(event.currentTarget, document.activeElement)) onOfferFocus(undefined);
    },
    onBlur: (event: ReactFocusEvent<HTMLLIElement>) => {
      if (!keep(event.currentTarget, event.relatedTarget)) onOfferFocus(undefined);
    },
  };
}

/** Um pedido de "traz este card pra frente" vindo do pino. Objeto novo a cada
 *  toque (e não só o id) porque tocar DUAS vezes o mesmo pino, com o trilho
 *  rolado no meio, tem de rolar de novo — id igual não acordaria o efeito. */
type DockScrollRequest = { readonly hotelId: string; readonly nonce: number };

/** Onde começa um item do trilho, na régua do próprio trilho. Os dois (`ul` e
 *  `li`) medem a partir do mesmo `offsetParent` — o palco do carrossel, que é
 *  `position: relative` —, então a diferença é a posição do card dentro da
 *  lista, calha incluída. A mesma régua serve para rolar ATÉ um card e para
 *  ler QUAL card está na frente, e é isso que fecha o círculo pino → trilho →
 *  pino sem os dois discordarem por uma calha. */
function dockItemStart(list: HTMLElement, item: HTMLElement): number {
  return item.offsetLeft - list.offsetLeft;
}

function dockPadding(list: HTMLElement): number {
  return Number.parseFloat(getComputedStyle(list).paddingLeft) || 0;
}

/** O card na FRENTE do trilho — o que o `scroll-snap-align: start` encosta na
 *  calha. Só existe quando o trilho rola: com todos os cards à vista não há
 *  "o card em foco", e acender um pino fixo ali seria inventar uma escolha. */
function dockLeadingHotelId(list: HTMLElement): string | undefined {
  if (list.scrollWidth <= list.clientWidth + 8) return undefined;
  const edge = list.scrollLeft + dockPadding(list);
  let leading: { id: string; distance: number } | undefined;
  for (const item of list.querySelectorAll<HTMLElement>('[data-hotel-id]')) {
    const distance = Math.abs(dockItemStart(list, item) - edge);
    const id = item.dataset.hotelId;
    if (id !== undefined && (leading === undefined || distance < leading.distance)) {
      leading = { id, distance };
    }
  }
  return leading?.id;
}

/**
 * O par pino ↔ card do dock, nos dois sentidos, com um só destaque no mapa.
 *
 * Duas fontes acendem o pino: o gesto de HOVER/FOCO num card (transitório, e
 * some quando o gesto solta) e o card EM FOCO NO TRILHO — o que está na frente
 * depois de rolar, ou o que um toque no pino trouxe pra frente. O hover vence
 * enquanto dura; solto, o pino volta para o card do trilho em vez de apagar.
 *
 * O toque no pino faz as duas coisas: acende o pino daquele hotel e pede ao
 * trilho que traga o card dele pra frente (`scrollRequest`). O `onScroll` do
 * trilho então confirma o mesmo id quando a rolagem assenta.
 */
function useDockFocus(offers: readonly HotelOfferView[]) {
  const [hoverHotelId, setHoverHotelId] = useState<string>();
  const [leadingHotelId, setLeadingHotelId] = useState<string>();
  const [scrollRequest, setScrollRequest] = useState<DockScrollRequest>();
  /* O foco vale só enquanto o hotel está na lista: quem some por corte ou nova
     busca desmonta calado, e o id ficaria preso acendendo um pino que voltaria
     aceso quando o hotel reaparecesse. */
  const [listed, setListed] = useState(offers);
  if (listed !== offers) {
    setListed(offers);
    const keepIfListed = (current?: string) => offers.find((o) => o.id === current)?.id;
    setLeadingHotelId(keepIfListed);
    setHoverHotelId(keepIfListed);
  }
  const focusFromPin = useCallback((hotelId: string) => {
    /* O toque no pino é a escolha mais recente, e vence o card que ainda segura
       o foco do teclado (tocar num card deixa o foco nele, e `blur` não vem):
       sem soltar o hover aqui, o pino tocado rolava o trilho mas o destaque
       ficava preso no card anterior. */
    setHoverHotelId(undefined);
    setLeadingHotelId(hotelId);
    setScrollRequest((current) => ({ hotelId, nonce: (current?.nonce ?? 0) + 1 }));
  }, []);
  return {
    activeHotelId: hoverHotelId ?? leadingHotelId,
    scrollRequest,
    onOfferFocus: setHoverHotelId,
    onLeadingChange: setLeadingHotelId,
    focusFromPin,
  };
}

/** `warning` para falha transitória (timeout), `danger` para o resto. */
type HotelResultsNoticeTone = 'danger' | 'warning';

export type HotelResultsProps = {
  state: 'loading' | 'ready' | 'empty' | 'error';
  copy: HotelResultsCopy;
  message?: string;
  noticeTitle?: string;
  noticeTone?: HotelResultsNoticeTone;
  /** Não há critério completo pra re-buscar: o retry vira clique morto, então
   *  some do aviso e sobra a saída que resolve — ajustar a busca. */
  retryUnavailable?: boolean;
  offers: readonly HotelOfferView[];
  selectedHotelId?: string;
  map: HotelMapView;
  search: HotelSearchBarProps;
  readOnly?: boolean;
  busy?: boolean;
  /** `'map'` (default) é o palco de RESULTADO: barra flutuando sobre o mapa,
   *  dock de cards por cima e o rodapé de "ver todos". `'plain'` é o passo de
   *  BUSCA da página determinística — só o formulário, no fluxo. Antes da
   *  primeira busca o mapa não tem pino a mostrar, e ocupando a primeira dobra
   *  ele empurrava os atalhos para fora dela. O card da conversa segue no
   *  default. */
  layout?: 'map' | 'plain';
  /** O coração nos cards do dock (ver `HotelFavoriteToggle`). Ausente ⇒ nenhum
   *  coração — o gate do caminho conversacional mora no host. */
  favoriteToggle?: HotelFavoriteToggle;
  /** No rodapé, ao lado do "Ver todos os hotéis". Ausente ⇒ nenhum botão. */
  share?: HotelResultsShare;
  onRetry(): void;
  onMapExpand(): void;
  onSeeAll(): void;
  onHotelSelect(hotelId: string): void;
  onDetailsOpen(hotelId: string): void;
};

/**
 * A espera na ANATOMIA do card do dock, não uma frase no vão — doutrina do
 * `EsqueletoDoDock` do protótipo: o esqueleto promete a FORMA do que vem (foto
 * quadrada de 64, nome sobre nota·distância, preço na ponta, a linha de
 * comodidades), e retângulo liso promete só altura.
 *
 * Ele monta as MESMAS classes do card e do trilho reais em vez de repetir as
 * medidas: calha, raio, vidro e largura por card saem de `hotel_results.css`, e
 * é o que garante que o dock não pule de altura quando as ofertas chegam.
 * Trocou a anatomia do card? troca aqui.
 *
 * A frase da espera é VISÍVEL — um chip com girador sobre o trilho — porque o
 * esqueleto pulsando sozinho fazia a pessoa ler "cards vazios/travados" (#2444).
 * Um `<output>` (papel `status` implícito) SÓ a mantém como anúncio único ao
 * leitor de tela, e o trilho de esqueletos segue `aria-hidden` para não duplicar a fala.
 * `pointer-events-none` no trilho para o mapa continuar arrastável por baixo.
 */
function HotelResultsSkeleton({ message }: { readonly message?: string }): ReactNode {
  return (
    <>
      {message && (
        <output className="hui-hotel-results-searching">
          <LoaderCircle className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {message}
        </output>
      )}
      <ul
        className="hui-hotel-results-list pointer-events-none"
        data-view="inline"
        aria-hidden="true"
      >
        {[0, 1, 2].map((slot) => (
          <li key={slot}>
            <div className="hui-hotel-offer-card" data-surface="map">
              <div className="hui-hotel-offer-main">
                <Skeleton className="hui-hotel-offer-photo rounded-xl" />
                <div className="hui-hotel-offer-copy grid gap-1.5">
                  <div className="hui-hotel-offer-heading">
                    <div className="grid gap-1">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <div className="grid justify-items-end gap-1">
                      <Skeleton className="h-4 w-14" />
                      <Skeleton className="h-3 w-8" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-40" />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

function noticeIcon(state: HotelResultsProps['state'], tone: HotelResultsNoticeTone) {
  if (state !== 'error') return <SearchX aria-hidden="true" />;
  return tone === 'warning' ? <Clock aria-hidden="true" /> : <TriangleAlert aria-hidden="true" />;
}

/** As saídas do aviso. Componente próprio (e não `&&` inline no aviso) porque
 *  `HotelResultsNotice` já vive no teto de complexidade do lint. Sem saída
 *  nenhuma — vazio no palco estreito — a fileira nem nasce, pra não deixar a
 *  margem de cima solta embaixo do texto. */
function HotelNoticeActions({
  retryAvailable,
  disabled,
  copy,
  onRetry,
  onAdjust,
}: Pick<HotelResultsProps, 'copy' | 'onRetry'> & {
  readonly retryAvailable: boolean;
  readonly disabled: boolean;
  readonly onAdjust?: () => void;
}): ReactNode {
  if (!retryAvailable && !onAdjust) return null;
  return (
    <div className="col-start-2 mt-2 flex flex-wrap gap-2">
      {retryAvailable && (
        <Button size="sm" disabled={disabled} onClick={onRetry}>
          {copy.retryLabel}
        </Button>
      )}
      {onAdjust && (
        <Button size="sm" variant="outline" disabled={disabled} onClick={onAdjust}>
          {copy.adjustLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * O aviso é a PEÇA do dock quando não há oferta — não uma frase no vão. Ele
 * ocupa a mesma faixa que o trilho de cards ocuparia, mas NÃO reusa o trilho:
 * `hui-hotel-results-list` é `flex` com rolagem horizontal e largura fixa por
 * item (21rem), e um aviso ali nasceria como coluna rolável estourando o palco.
 * Aqui é bloco simples dentro do painel, então a largura é a do palco menos a
 * calha, e ponto.
 *
 * Sem shell de card em volta: o `Alert` já traz superfície opaca (o `soft` do
 * DS), e empilhar os dois desenhava um anel de vidro em torno da tinta.
 */
function HotelResultsNotice({
  state,
  message,
  title,
  copy,
  tone,
  actionsDisabled,
  retryUnavailable,
  onRetry,
  onAdjust,
}: Pick<HotelResultsProps, 'state' | 'message' | 'copy' | 'retryUnavailable' | 'onRetry'> & {
  readonly title?: string;
  readonly tone: HotelResultsNoticeTone;
  readonly actionsDisabled: boolean;
  /** Ausente ⇒ sem "Ajustar busca": no palco estreito o aviso já vem COM o
   *  formulário embaixo (ver `HotelSearchOnly`), e um botão levando a ele seria
   *  saída pra onde a pessoa já está. */
  readonly onAdjust?: () => void;
}): ReactNode {
  if (state === 'loading') return <HotelResultsSkeleton message={message} />;
  if (state !== 'empty' && state !== 'error') return null;
  // Vazio SEM copy é o primeiro turno (ainda não buscou): o palco é só a barra
  // de busca, e um aviso solto ali seria um alarme sobre nada. Erro sem copy
  // ainda aparece — a saída de retry é o que importa ali.
  const failed = state === 'error';
  if (!failed && !title && !message) return null;
  return (
    <Alert
      className="hui-hotel-results-notice"
      variant="soft"
      color={failed ? tone : 'neutral'}
      // O vazio é resultado da busca, não incidente: `status` não interrompe a
      // leitura em curso como `alert` (que o primitivo assume) faria.
      {...(failed ? {} : { role: 'status' })}
    >
      {noticeIcon(state, tone)}
      {title && <AlertTitle>{title}</AlertTitle>}
      {message && <AlertDescription>{message}</AlertDescription>}
      <HotelNoticeActions
        retryAvailable={failed && !retryUnavailable}
        disabled={actionsDisabled}
        copy={copy}
        onRetry={onRetry}
        {...(onAdjust ? { onAdjust } : {})}
      />
    </Alert>
  );
}

function HotelResultsList({
  copy,
  offers,
  selectedHotelId,
  readOnly,
  busy,
  favoriteToggle,
  onOfferFocus,
  onLeadingChange,
  scrollRequest,
  onHotelSelect,
  onDetailsOpen,
}: Omit<HotelResultsProps, 'state' | 'message' | 'map' | 'search' | 'onRetry'> & {
  readonly onOfferFocus: (hotelId?: string) => void;
  readonly onLeadingChange: (hotelId?: string) => void;
  readonly scrollRequest: DockScrollRequest | undefined;
}): ReactNode {
  const { listRef, available, measure, scroll } = useRail();
  /* O dock sai de cena quando a busca recomeça (vira esqueleto) ou falha, e
     `mouseleave` não dispara em quem desmonta sob o cursor: sem esta limpeza o
     pino do último card em hover (ou na frente do trilho) ficava aceso sozinho
     no mapa. */
  useEffect(
    () => () => {
      onOfferFocus(undefined);
      onLeadingChange(undefined);
    },
    [onLeadingChange, onOfferFocus],
  );
  /* Quem lê o card da frente é o próprio trilho — ao montar, quando as ofertas
     mudam e a cada rolagem. `onScroll` só avisa quando o id MUDA: um `setState`
     por evento de scroll re-renderizava no meio da rolagem suave e a matava
     (o protótipo registrou o defeito). */
  const measureLeading = useCallback(() => {
    const list = listRef.current;
    if (list) onLeadingChange(dockLeadingHotelId(list));
  }, [listRef, onLeadingChange]);
  useEffect(measureLeading, [measureLeading, offers]);
  /* E quando o trilho muda de tamanho: alargar a tela até todos os cards
     caberem não dispara scroll, e o card da frente deixaria um pino aceso sem
     haver mais "frente". */
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measureLeading);
    observer.observe(list);
    return () => {
      observer.disconnect();
    };
  }, [listRef, measureLeading]);
  /* O pino pediu um card: rola até o início dele, calha descontada, que é a
     mesma posição em que o `scroll-snap` o deixaria. */
  useEffect(() => {
    const list = listRef.current;
    if (!list || !scrollRequest) return;
    const item = Array.from(list.querySelectorAll<HTMLElement>('[data-hotel-id]')).find(
      (candidate) => candidate.dataset.hotelId === scrollRequest.hotelId,
    );
    if (!item) return;
    list.scrollTo({ left: dockItemStart(list, item) - dockPadding(list), behavior: 'smooth' });
  }, [listRef, scrollRequest]);
  const list = (
    <ul
      ref={listRef}
      className="hui-hotel-results-list"
      data-view="inline"
      aria-label={copy.resultsLabel}
      onScroll={() => {
        measure();
        measureLeading();
      }}
    >
      {offers.map((offer) => (
        /* O par card ↔ pino, como no protótipo: passar o mouse (ou o foco) no
           card acende o pino do mesmo hotel no mapa. Os gestos moram no item da
           lista e não no card para o alvo cobrir o selo, que sangra a borda —
           entrar por cima dele não pode apagar o pino que ele mesmo acendeu. */
        <li key={offer.id} data-hotel-id={offer.id} {...offerFocusHandlers(offer.id, onOfferFocus)}>
          <HotelOfferCard
            offer={offer}
            detailsLabel={copy.detailsLabel}
            selectLabel={copy.selectLabel}
            action={readOnly ? 'details' : 'select'}
            surface="map"
            selected={selectedHotelId === offer.id}
            readOnly={readOnly}
            busy={busy}
            {...(favoriteToggle ? { favoriteToggle } : {})}
            onHotelSelect={onHotelSelect}
            onDetailsOpen={onDetailsOpen}
          />
        </li>
      ))}
    </ul>
  );
  return (
    <div className="hui-hotel-results-carousel">
      {list}
      {available.previous && (
        <RailArrow
          direction="previous"
          label={copy.previousResultsLabel}
          onClick={() => {
            scroll(-1);
          }}
        />
      )}
      {available.next && (
        <RailArrow
          direction="next"
          label={copy.nextResultsLabel}
          onClick={() => {
            scroll(1);
          }}
        />
      )}
    </div>
  );
}

/** O estado EFETIVO das saídas do aviso, não só o do card: "Ajustar busca" e
 *  "Tentar novamente" mexem na barra, e uma barra congelada por conta própria
 *  (`search.readOnly`/`search.busy`) deixaria os botões vivos mirando um
 *  controle desabilitado. */
function noticeActionsDisabled(
  props: Pick<HotelResultsProps, 'readOnly' | 'busy' | 'search'>,
): boolean {
  return (
    props.readOnly === true ||
    props.busy === true ||
    props.search.readOnly === true ||
    props.search.busy === true
  );
}

function HotelResultsPanel({
  onAdjust,
  ...props
}: HotelResultsProps & {
  readonly onAdjust: () => void;
  readonly onOfferFocus: (hotelId?: string) => void;
  readonly onLeadingChange: (hotelId?: string) => void;
  readonly scrollRequest: DockScrollRequest | undefined;
}): ReactNode {
  if (props.state === 'ready') {
    return <HotelResultsList {...props} />;
  }
  return (
    <HotelResultsNotice
      {...props}
      {...(props.noticeTitle ? { title: props.noticeTitle } : {})}
      tone={props.noticeTone ?? 'danger'}
      actionsDisabled={noticeActionsDisabled(props)}
      onAdjust={onAdjust}
    />
  );
}

function resultMapControls({
  copy,
  canExpand,
  onExpand,
}: {
  readonly copy: HotelResultsCopy;
  readonly canExpand: boolean;
  readonly onExpand: () => void;
}): NonNullable<HotelMapProps['controls']> {
  return {
    zoomInLabel: copy.mapZoomInLabel,
    zoomOutLabel: copy.mapZoomOutLabel,
    recenterLabel: copy.mapRecenterLabel,
    ...(canExpand ? { expandLabel: copy.mapExpandLabel, onExpand } : {}),
  };
}

/** As duas saídas para as superfícies grandes (mapa cheio e lista completa) só
 *  existem enquanto o card responde: um card congelado — conversa encerrada, ou
 *  superado por outro que decide o assunto — é registro do que foi, e abrir uma
 *  tela cheia dele só oferece navegação que termina em botão morto.
 *
 *  `busy` não conta, embora chegue somado ao `readOnly`: o turno em voo é
 *  transitório e volta sozinho, e some/aparece a cada busca faria o rodapé
 *  piscar. Congelado é o que segue `readOnly` com o turno já fechado. */
function canBrowseSurfaces(props: {
  readonly state: HotelResultsProps['state'];
  readonly offerCount: number;
  readonly readOnly: boolean;
  readonly busy: boolean;
}) {
  const frozen = props.readOnly && !props.busy;
  return !frozen && props.state === 'ready' && props.offerCount > 0;
}

/** O passo de BUSCA da página determinística (`layout='plain'`): só o
 *  formulário, no fluxo. Componente próprio e não um ramo dentro de
 *  `HotelResults` porque aquela função já vive no teto de complexidade do lint
 *  — e porque não há nada em comum entre os dois palcos além da barra. */
/** O formulário da PÁGINA determinística: sem a pílula-card da conversa
 *  (`naked`) e com o preenchimento guiado (`guided` — escolher a cidade abre as
 *  datas). Derivado de `layout='plain'` e não de props novas do organismo: não
 *  há palco `plain` que queira a casca do card nem o formulário inerte. A
 *  cápsula do palco de mapa recebe o `guided` que vier em `search` — é o
 *  organismo quem decide (passo de busca, card não superado). */
const PAGE_FORM = { naked: true, guided: true } as const;

function HotelSearchOnly({
  search,
  readOnly,
  busy,
  shell,
  notice,
}: Pick<HotelResultsProps, 'search' | 'readOnly' | 'busy'> & {
  /** `'card'` veste o formulário com a casca de card da conversa — o palco
   *  estreito (#2671), onde ele substitui o mapa: no primeiro turno o mapa vazio,
   *  e em vazio/erro o mapa sem pino. A página determinística segue sem casca:
   *  lá o formulário está no papel. */
  readonly shell?: 'card';
  /** O aviso de vazio/erro em cima do formulário, no palco estreito. Sem barra
   *  flutuante ali não há "Ajustar busca" a levar, então a saída é os próprios
   *  campos logo abaixo — antes o card vazio não tinha saída nenhuma. */
  readonly notice?: ReactNode;
}): ReactNode {
  return (
    <section className="genui2 hui-hotel-results" data-layout="plain" data-shell={shell}>
      {notice}
      <div className="hui-hotel-results-search">
        {/* O OU das duas fontes, como no palco de resultado: `readOnly` do card
            e `search.readOnly` do próprio form travam a barra cada um por sua
            conta, e ler só uma delas destravaria a outra. */}
        <HotelSearchBar
          {...search}
          {...PAGE_FORM}
          readOnly={readOnly === true || search.readOnly === true}
          busy={busy === true || search.busy === true}
        />
      </div>
    </section>
  );
}

/** A barra de busca flutua no topo do palco e o dock de cards cobre a base: o
 *  enquadramento tem que descontar os dois, senão nasce pino atrás deles. Cada
 *  número é a borda de baixo da peça mais a calha de 12: a busca termina em 60
 *  (12 de topo + 48 de pílula) e o dock ocupa 140 com a calha dele já dentro.
 *  Sem barra (palco estreito) sobra só a calha do topo, e os pinos ganham a
 *  faixa que ela ocupava. */
function mapOverlays(narrow: boolean): NonNullable<HotelMapProps['overlays']> {
  return { top: narrow ? 12 : 72, bottom: 140 };
}

/** O aviso de vazio/erro sobre o formulário do palco estreito. No primeiro
 *  turno (vazio sem copy) `HotelResultsNotice` já devolve nada — a mesma
 *  leitura de "ainda não buscou" que ele faz no palco do mapa. */
function CompactNotice(props: Readonly<HotelResultsProps>): ReactNode {
  return (
    <HotelResultsNotice
      state={props.state}
      message={props.message}
      copy={props.copy}
      {...(props.noticeTitle ? { title: props.noticeTitle } : {})}
      {...(props.retryUnavailable ? { retryUnavailable: props.retryUnavailable } : {})}
      tone={props.noticeTone ?? 'danger'}
      actionsDisabled={noticeActionsDisabled(props)}
      onRetry={props.onRetry}
    />
  );
}

/** A barra flutuando no topo do palco de resultado. No palco ESTREITO ela SAI
 *  (#2671): com resultado, refinar é escrever no composer, como nos chats de
 *  referência, e a pílula em cima do mapa só roubava área de pino. Sem
 *  resultado o palco nem é este — ver `HotelResults`. O OU das duas fontes de
 *  trava: `readOnly`/`busy` do card e os da própria barra travam cada um por
 *  sua conta, e ler só uma delas destravaria a outra. */
function StageSearchBar({
  narrow,
  searchRef,
  search,
  readOnly,
  busy,
}: {
  readonly narrow: boolean;
  readonly searchRef: RefObject<HTMLDivElement | null>;
  readonly search: HotelSearchBarProps;
  readonly readOnly: boolean;
  readonly busy: boolean;
}): ReactNode {
  return narrow ? null : (
    <div ref={searchRef} className="hui-hotel-results-search">
      <HotelSearchBar
        {...search}
        readOnly={readOnly || search.readOnly}
        busy={busy || search.busy}
      />
    </div>
  );
}

/** O palco de RESULTADO: barra flutuando sobre o mapa, dock de cards e o
 *  rodapé de "ver todos". */
function HotelResultsStage({
  state,
  copy,
  message,
  noticeTitle,
  noticeTone,
  retryUnavailable,
  offers,
  selectedHotelId,
  map,
  search,
  readOnly = false,
  busy = false,
  favoriteToggle,
  share,
  onRetry,
  onMapExpand,
  onSeeAll,
  onHotelSelect,
  onDetailsOpen,
  narrow,
}: Readonly<HotelResultsProps & { narrow: boolean }>): ReactNode {
  const browsable = canBrowseSurfaces({ state, offerCount: offers.length, readOnly, busy });
  const searchRef = useRef<HTMLDivElement>(null);
  const dockFocus = useDockFocus(offers);
  /* "Ajustar busca" leva ao DESTINO: é o critério que mais muda depois de uma
     falha, e sem essa ponte as duas saídas (o submit da barra e o retry do
     aviso) ficam concorrendo mudas. `click` e não `focus`: o destino é a
     pílula-gatilho do `PlaceField`, e é o clique que a abre — aberta, o input
     da busca nasce focado dentro dela. No palco estreito a barra é a
     pílula-resumo, e o clique nela abre a folha no campo pendente. */
  const openSearch = useCallback(() => {
    searchRef.current
      ?.querySelector<HTMLElement>('[data-place-field-trigger], [data-hotel-search-summary]')
      ?.click();
  }, []);

  return (
    <section
      className="genui2 hui-hotel-results"
      aria-busy={busy || state === 'loading' || undefined}
      data-view="inline"
    >
      <div className="hui-hotel-results-layout">
        <div className="hui-hotel-results-stage" data-state={state}>
          <StageSearchBar
            narrow={narrow}
            searchRef={searchRef}
            search={search}
            readOnly={readOnly}
            busy={busy}
          />
          <HotelMap
            map={map}
            offers={offers}
            overlays={mapOverlays(narrow)}
            selectLabel={copy.selectLabel}
            photoCopy={{
              previousPhotoLabel: copy.previousPhotoLabel,
              nextPhotoLabel: copy.nextPhotoLabel,
            }}
            detailsLabel={copy.detailsLabel}
            selectedHotelId={selectedHotelId}
            {...(dockFocus.activeHotelId ? { activeHotelId: dockFocus.activeHotelId } : {})}
            selectionLocked={readOnly || busy}
            /* Sem controles em erro: o mapa não tem pino nem enquadramento a
               oferecer, e um zoom focável ali só rouba a vez do aviso. */
            {...(state === 'error'
              ? {}
              : {
                  controls: resultMapControls({
                    copy,
                    canExpand: browsable,
                    onExpand: onMapExpand,
                  }),
                })}
            onHotelSelect={onHotelSelect}
            onDetailsOpen={onDetailsOpen}
            onHotelFocus={dockFocus.focusFromPin}
          />
          <div className="hui-hotel-results-panel" data-overlay="true">
            <HotelResultsPanel
              state={state}
              copy={copy}
              message={message}
              {...(noticeTitle ? { noticeTitle } : {})}
              {...(noticeTone ? { noticeTone } : {})}
              {...(retryUnavailable ? { retryUnavailable } : {})}
              offers={offers}
              selectedHotelId={selectedHotelId}
              map={map}
              search={search}
              readOnly={readOnly}
              busy={busy}
              {...(favoriteToggle ? { favoriteToggle } : {})}
              onRetry={onRetry}
              onMapExpand={onMapExpand}
              onSeeAll={onSeeAll}
              onHotelSelect={onHotelSelect}
              onDetailsOpen={onDetailsOpen}
              onAdjust={openSearch}
              onOfferFocus={dockFocus.onOfferFocus}
              onLeadingChange={dockFocus.onLeadingChange}
              scrollRequest={dockFocus.scrollRequest}
            />
          </div>
        </div>
      </div>
      {browsable && (
        <HotelResultsFooter
          expandLabel={copy.expandLabel}
          offers={offers}
          share={share}
          onSeeAll={onSeeAll}
        />
      )}
    </section>
  );
}

/** O componente público é só a chave entre os palcos — o de resultado
 *  (default), o formulário solto do passo de busca da página determinística e,
 *  no palco ESTREITO, o formulário em casca de card sempre que NÃO há resultado
 *  (#2671): no celular o mapa desfocado com a pílula em cima era um card vazio
 *  que pedia um toque a mais, e o padrão do aéreo é trazer os campos direto. O
 *  vazio e o erro entram na mesma casca, com o aviso em cima: o mapa sem pino
 *  não tinha nada a mostrar, e sem barra flutuante a pessoa ficava sem saída
 *  pra mudar a busca. Fino de propósito: `HotelResultsStage` já vive no teto de
 *  complexidade do lint, e o ramo somado lá dentro o estourava. */
export function HotelResults(props: Readonly<HotelResultsProps>): ReactNode {
  const narrow = useMediaQuery(HOTEL_NARROW_QUERY);
  if (props.layout === 'plain') return <HotelSearchOnly {...props} />;
  if (narrow && (props.state === 'empty' || props.state === 'error')) {
    return <HotelSearchOnly {...props} shell="card" notice={<CompactNotice {...props} />} />;
  }
  return <HotelResultsStage {...props} narrow={narrow} />;
}
